import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { buildAsyncReducers } from '../thunkTemplate';
import { push } from 'redux-first-history';
import { Auth, Amplify } from 'aws-amplify';
import { toastr } from 'react-redux-toastr';

import { auth as initialState } from '../initialState';
import { setUser } from '../user';
import { setLoader } from '../pages';
import ROUTES from '../../constants/routes';

export const defaultAmplifyConfig = {
  Auth: {
    region: process.env.REACT_APP_REGION,
    userPoolId: process.env.REACT_APP_USER_POOL_ID_OLD,
    userPoolWebClientId: process.env.REACT_APP_USER_POOL_WEBCLIENT_ID_OLD,
  },
};

export const configureAmplifyAuth = (payload = defaultAmplifyConfig) =>
  Amplify.configure(payload);

const userState = async (user) => ({
  user,
  authenticated: Boolean(user),
  currentSession: await Auth.currentSession(),
});

const configure = createAsyncThunk(
  'auth/configure',
  async (_, { dispatch, getState }) => {
    dispatch(setLoader(true));

    let user = initialState.user;

    try {
      user = await Auth.currentAuthenticatedUser();
      if (!user) {
        dispatch(push(ROUTES.UNAUTH.AUTH));
      }
      dispatch(setUser(user));
      return await userState(user);
    } catch (err) {
      dispatch(setLoader(false));
      dispatch(push(ROUTES.UNAUTH.AUTH));
    }
  }
);

const login = createAsyncThunk(
  'auth/login',
  async (payload, { dispatch, getState }) => {
    const { email, password } = payload;

    let user = initialState.user;
    try {
      configureAmplifyAuth(defaultAmplifyConfig);
      dispatch(setLoader(true));
      user = await Auth.signIn(email, password);
      await dispatch(setUser(user));

      dispatch(push(ROUTES.AUTH.PORTFOLIO));
    } catch (error) {
      dispatch(push(ROUTES.UNAUTH.AUTH));
      dispatch(setLoader(false));
      toastr.error('Error', error.message ? error.message : error);
    }

    return await userState(user);
  }
);

const logout = createAsyncThunk('auth/logout', async (_, { dispatch }) => {
  const user = initialState.user;

  try {
    setLoader(true);
    await Auth.signOut();
    dispatch(push(ROUTES.UNAUTH.AUTH));
    dispatch(setUser(user));
  } catch (err) {
    dispatch(push(ROUTES.UNAUTH.AUTH));
  } finally {
    setLoader(false);
  }
});

// NOTE: "Mutating" state is safe in redux toolkit because it uses Immer
const { reducer } = createSlice({
  name: 'auth',
  initialState,
  extraReducers: (builder) => {
    buildAsyncReducers(builder, [configure, login, logout]);
  },
});

// Export the reducer, either as a default or named export
export default reducer;
export { configure, login, logout };
